<template>
  <div class="account-panel" :lang="$domLang">
    <!-- 회원 정보 -->
    <section class="account-card">
      <div class="account-profile">
        <v-img class="account-profile__image rounded-circle" :src="lafiaUser_profile_img_path" alt="" />
        <button type="button" class="account-link" @click="dialog.updateImage = true">{{ $t("page.mypage.info.change-photo") }}</button>
        <v-dialog v-model="dialog.updateImage" width="auto">
          <ProfileImage
            @imageUpdated="
              load()
              dialog.updateImage = false
            "
          />
        </v-dialog>
      </div>

      <dl class="account-facts">
        <div class="account-fact">
          <dt>{{ $t("page.mypage.info.ID") }}</dt>
          <dd>{{ getId }}</dd>
        </div>
        <div class="account-fact">
          <dt>{{ $t("page.mypage.info.name") }}</dt>
          <dd>{{ $getUserDisplayName($store.state.lafia_user?.name) }}</dd>
        </div>
        <div v-if="!isSnsUser" class="account-fact">
          <dt>{{ $t("page.mypage.info.email") }}</dt>
          <dd>{{ user.email || $store.state.lafia_user.email }}</dd>
        </div>
        <div v-if="isSnsUser && snsProviderName" class="account-fact">
          <dt>{{ $t("page.mypage.info.login-method") }}</dt>
          <dd>{{ $t("page.mypage.info.login-with", { provider: snsProviderName }) }}</dd>
        </div>
      </dl>

      <!-- 연락처: 바꿨을 때만 저장 버튼이 켜진다 -->
      <label class="account-label" for="account-phone">{{ $t("page.mypage.info.phone") }}</label>
      <div class="account-phone">
        <v-text-field
          id="account-phone"
          class="account-input"
          outlined
          dense
          v-model.trim="phone"
          type="tel"
          inputmode="numeric"
          autocomplete="tel"
          hide-spin-buttons
          @blur="$v.phone.$touch()"
          :error-messages="phone_Errors"
        ></v-text-field>
        <v-btn class="account-button account-button--primary" depressed :disabled="!phoneChanged || isSubmitting" :loading="saving === 'phone'" @click="savePhone()">
          {{ $t("page.mypage.info.save-phone") }}
        </v-btn>
      </div>
    </section>

    <!-- 비밀번호: 바꿀 때만 펼친다(SNS 로그인 회원은 없음) -->
    <section v-if="!isSnsUser" class="account-card">
      <button type="button" class="account-toggle" :aria-expanded="String(isPasswordOpen)" aria-controls="account-password" @click="isPasswordOpen = !isPasswordOpen">
        <span>{{ $t("page.mypage.info.change-password") }}</span>
        <v-icon size="20">{{ isPasswordOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
      </button>
      <div v-show="isPasswordOpen" id="account-password" class="account-password">
        <label class="account-label" for="account-password-prev">{{ $t("page.mypage.info.curr-password") }}</label>
        <v-text-field id="account-password-prev" class="account-input" outlined dense type="password" autocomplete="current-password" v-model="password_prev" @blur="$v.password_prev.$touch()" :error-messages="password_prev_Errors"></v-text-field>
        <label class="account-label" for="account-password-next">{{ $t("page.mypage.info.new-password") }}</label>
        <v-text-field id="account-password-next" class="account-input" outlined dense type="password" autocomplete="new-password" v-model="password_next" @blur="$v.password_next.$touch()" :error-messages="password_next_Errors"></v-text-field>
        <label class="account-label" for="account-password-check">{{ $t("page.mypage.info.new-password-confirm") }}</label>
        <v-text-field id="account-password-check" class="account-input" outlined dense type="password" autocomplete="new-password" v-model="password_check" @blur="$v.password_check.$touch()" :error-messages="password_check_Errors"></v-text-field>
        <v-btn class="account-button account-button--primary account-button--block" depressed :disabled="isSubmitting" :loading="saving === 'password'" @click="changePassword()">
          {{ $t("page.mypage.info.change-password") }}
        </v-btn>
      </div>
    </section>

    <!-- 로그아웃은 따로, 탈퇴는 작은 글자 -->
    <v-btn class="account-button account-button--quiet account-button--block" outlined to="/auth/logout">{{ $t("button.logout") }}</v-btn>
    <button type="button" class="account-link account-link--withdraw" @click="dialog.alert = true">{{ $t("button.withdraw-membership") }}</button>
    <p class="account-version">ver {{ appVer }}</p>

    <!-- 탈퇴 확인 -->
    <v-dialog v-model="dialog.alert" width="auto" content-class="rounded-10" style="z-index: 999">
      <v-sheet class="rounded-10 pa-4">
        <p class="text-center mb-0">
          <v-icon size="80" color="#f2b8bf" class="">mdi-alert-circle-outline</v-icon>
        </p>

        <p class="text-center text-caption mt-3 px-1" style="letter-spacing: 0px !important" v-html="$t('page.mypage.info.user_withdraw_guide_text')"></p>

        <div class="d-flex justify-center">
          <v-checkbox color="#f2b8bf" dense v-model="agree" :label="$t('page.mypage.info.confirm_check_withdraw_label')"></v-checkbox>
        </div>

        <div class="d-flex justify-space-between mt-2">
          <v-btn color="#ddd" class="rounded-10" dark width="48%" depressed @click="dialog.alert = false"> {{ $t("button.return-title") }} </v-btn>

          <v-btn :disabled="!agree || isSubmitting" :loading="saving === 'withdraw'" color="#f2b8bf" style="color: white !important" class="rounded-10" width="48%" depressed @click="leave()"> {{ $t("button.withdraw") }} </v-btn>
        </div>
      </v-sheet>
    </v-dialog>
  </div>
</template>
<script>
import ProfileImage from "./ProfileImage"

// 검증
import { validationMixin } from "vuelidate"
import { required, sameAs, minLength } from "vuelidate/lib/validators"
import { mapState, mapMutations, mapGetters } from "vuex"
import authService from "@/api/auth-service"
import { AppVersion } from "@/constant"

function phoneNumberValidation(number) {
  return /^[0-9]{9,15}$/.test(String(number || "").trim())
}

// 예전 SNS 회원은 연락처 칸에 이 값이 저장되어 있다(서버 데이터 값이라 번역하지 않는다)
const LEGACY_SNS_PHONE = "SNS 로그인"

export default {
  components: {
    ProfileImage,
  },

  mixins: [validationMixin],

  validations: {
    phone: {
      required,
      phoneNumberValidation,
    },

    password_prev: {
      required,
      minLength: minLength(6),
    },

    password_next: {
      required,
      minLength: minLength(6),
    },

    password_check: {
      sameAsPassword: sameAs("password_next"),
    },
  },

  data: () => ({
    appVer: AppVersion,

    password_prev: "",
    password_next: "",
    password_check: "",
    phone: "",
    isPasswordOpen: false,
    saving: "",
    agree: false,

    user: {},

    dialog: {
      updateImage: false,
      alert: false,
    },
  }),

  computed: {
    ...mapState(["lafia_user"]),
    ...mapGetters(["lafiaUser_profile_img_path"]),

    isSubmitting() {
      return Boolean(this.saving)
    },

    getId() {
      return this.user.phone === LEGACY_SNS_PHONE ? this.user.email : this.user.user_id
    },

    snsProviderName() {
      const provider = String(this.user.provider_type || "").toLowerCase()
      if (!provider) return ""
      const key = `page.mypage.info.provider.${provider}`
      return this.$te(key) ? this.$t(key) : provider.charAt(0).toUpperCase() + provider.slice(1)
    },

    isSnsUser() {
      const providerType = String(this.user.provider_type || "").toLowerCase()
      if (providerType) return providerType !== "email" && providerType !== "normal"
      return this.user.phone === LEGACY_SNS_PHONE
    },

    phoneChanged() {
      return String(this.phone || "").trim() !== String(this.user.phone || "")
    },

    // 현재 비밀번호 Error 문구
    password_prev_Errors() {
      const errors = []
      if (!this.$v.password_prev.$dirty) return errors
      !this.$v.password_prev.required && errors.push(this.$t("page.mypage.info.input-current-password-hint"))
      !this.$v.password_prev.minLength && errors.push(this.$t("page.mypage.info.password-min-length-rule"))
      return errors
    },

    // 신규 비밀번호 Error 문구
    password_next_Errors() {
      const errors = []
      if (!this.$v.password_next.$dirty) return errors
      ;(!this.$v.password_next.required || !this.$v.password_next.minLength) && errors.push(this.$t("page.mypage.info.password-min-length-rule"))
      return errors
    },

    // 신규 비밀번호 확인 Error 문구
    password_check_Errors() {
      const errors = []
      if (!this.$v.password_check.$dirty) return errors
      !this.$v.password_check.sameAsPassword && errors.push(this.$t("page.mypage.info.password-mismatch-newpassword"))
      return errors
    },

    // 연락처 Error 문구
    phone_Errors() {
      const errors = []
      if (!this.$v.phone.$dirty) return errors
      if (!this.$v.phone.required || !this.$v.phone.phoneNumberValidation) {
        errors.push(this.$t("page.findId.warning-input-phone"))
      }
      return errors
    },
  },

  mounted() {
    this.load()
  },

  methods: {
    ...mapMutations(["logout"]),

    async load() {
      const user_id = this.lafia_user.user_id
      if (!user_id) {
        this.logout()
        this.$router.replace("/auth/login").catch(() => {})
        return
      }
      try {
        const res = await authService.getUser(user_id)
        if (!res) {
          alert(this.$t("alert.user-not-found"))
          this.logout()
          this.$router.replace({ path: "/auth/login", query: { callbackUrl: this.$route.fullPath } }).catch(() => {})
          return
        }
        this.user = res
        this.phone = String(this.user.phone || "")
      } catch (e) {
        alert(this.$t("alert.user-load-failed"))
      }
    },

    async savePhone() {
      this.$v.phone.$touch()
      if (this.$v.phone.$invalid || !this.phoneChanged || this.isSubmitting) return
      this.saving = "phone"
      try {
        const updated = await authService.updateUserPhoneNumber({ user_id: this.user.user_id, phone: String(this.phone).trim() })
        if (!updated) throw new Error("PHONE_UPDATE_FAILED")
        await this.load()
        this.$v.phone.$reset()
        alert(this.$t("alert.phone-saved"))
      } catch (e) {
        alert(this.$t("alert.error_fail_to_update_my_info"))
      } finally {
        this.saving = ""
      }
    },

    async changePassword() {
      this.$v.password_prev.$touch()
      this.$v.password_next.$touch()
      this.$v.password_check.$touch()
      if (this.$v.password_prev.$invalid || this.$v.password_next.$invalid || this.$v.password_check.$invalid || this.isSubmitting) return
      this.saving = "password"
      try {
        const matches = await authService.checkCurrentPassword(this.user.user_id, this.password_prev)
        if (!matches) {
          alert(this.$t("alert.error_mismatch_password"))
          return
        }
        const updated = await authService.updatePassword(this.user.user_id, this.password_next)
        if (!updated) throw new Error("PASSWORD_UPDATE_FAILED")
        this.password_prev = ""
        this.password_next = ""
        this.password_check = ""
        this.$v.password_prev.$reset()
        this.$v.password_next.$reset()
        this.$v.password_check.$reset()
        this.isPasswordOpen = false
        alert(this.$t("alert.password-changed"))
      } catch (e) {
        alert(this.$t("alert.error_fail_to_update_my_info"))
      } finally {
        this.saving = ""
      }
    },

    // 회원탈퇴: 확인 창의 체크가 곧 동의다(두 번 묻지 않는다)
    async leave() {
      if (!this.agree || this.isSubmitting) return
      this.saving = "withdraw"
      try {
        await this.$http.post("/api/user/delete", { params: { user_id: this.$store.state.lafia_user.user_id } })
        this.$gtag.event("delete_account", { method: this.user.provider_type || "" })
        this.dialog.alert = false
        alert(this.$t("alert.withdraw-complete"))
        this.$router.push("/auth/logout").catch(() => {})
      } catch (e) {
        alert(this.$t("alert.withdraw-failed"))
      } finally {
        this.saving = ""
      }
    },
  },
}
</script>
<style scoped>
/* 예약 내역 탭(quiet-schedule)과 같은 색·글꼴로 맞춘다 */
.account-panel {
  --account-ink: #5f4844;
  --account-muted: #8e7d78;
  --account-rose: #d74369;
  --account-line: rgba(189, 155, 92, 0.28);
  max-width: 480px;
  margin: 0 auto;
  padding: 22px 18px 48px;
  color: var(--account-ink);
  background: #fffdf9;
  font-family: "NanumSquare", sans-serif;
}
.account-panel:lang(ja) {
  font-family: "Noto Sans JP", sans-serif;
}
.account-card {
  margin-bottom: 14px;
  padding: 18px 16px;
  background: #fffaf2;
  border: 1px solid var(--account-line);
  border-radius: 14px;
}
.account-profile {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 14px;
}
.account-profile__image {
  flex: none;
  width: 56px;
  height: 56px;
  max-width: 56px;
}
.account-facts {
  margin: 0 0 16px;
}
.account-fact {
  display: flex;
  gap: 12px;
  padding: 9px 0;
  border-top: 1px solid var(--account-line);
  font-size: 14px;
}
.account-fact dt {
  flex: none;
  width: 84px;
  color: var(--account-muted);
}
.account-fact dd {
  min-width: 0;
  overflow-wrap: anywhere;
}
.account-label {
  display: block;
  margin: 0 0 6px;
  color: var(--account-muted);
  font-size: 13px;
}
.account-phone {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.account-phone .account-input {
  flex: 1;
  min-width: 0;
}
.account-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 44px;
  color: var(--account-ink);
  font-size: 15px;
  font-weight: 700;
  background: transparent;
  border: 0;
  cursor: pointer;
}
.account-password {
  padding-top: 10px;
}
.account-button.v-btn {
  height: 40px;
  border-radius: 7px;
  font-weight: 700;
  letter-spacing: 0;
}
.account-button--primary.v-btn {
  background: var(--account-rose) !important;
  color: #fff !important;
}
.account-button--primary.v-btn.v-btn--disabled {
  background: #f2efed !important;
  color: #b9aca8 !important;
}
.account-button--quiet.v-btn {
  color: var(--account-ink) !important;
  border-color: var(--account-line) !important;
  background: #fff !important;
}
.account-button--block.v-btn {
  width: 100%;
  margin-top: 6px;
}
.account-link {
  padding: 4px 0;
  color: var(--account-muted);
  font-size: 13px;
  text-decoration: underline;
  text-underline-offset: 3px;
  background: transparent;
  border: 0;
  cursor: pointer;
}
.account-link--withdraw {
  display: block;
  margin: 18px auto 0;
  font-size: 12px;
}
.account-version {
  margin: 10px 0 0;
  color: #c2b6b2;
  font-size: 11px;
  text-align: center;
}
.account-toggle:focus-visible,
.account-link:focus-visible {
  outline: 2px solid rgba(215, 67, 105, 0.45);
  outline-offset: 2px;
  border-radius: 6px;
}
::v-deep .account-input .v-input__slot {
  background: #fff !important;
}
::v-deep .account-input fieldset {
  border-color: #e6d9d2;
}
</style>
